import {
  ElementType,
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import sha1 from 'sha1';
import { DetailsModalContext, DetailsModalData, UserContext } from '../../../../context';
import { getCardModal, getChatCardModal } from '../../../../slices';
import { useAnalytics, useAppDispatch, useAppSelector, usePrevious } from '../../../hooks';
import { getStoryCardName, isKeyboardFocus } from '../../../utils';
import { IconLabel } from '../../IconLabel';
import { Modal } from '../../Modal';
import { DetailsContentProps } from '../DetailsContentCommon';

import classes from './DetailsModalCommon.module.scss';

interface DetailsModalCommonProps {
  ContentComponent: ElementType<DetailsContentProps>;
}

export const DetailsModalCommon: FunctionComponent<DetailsModalCommonProps> = memo(
  ({ ContentComponent }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const { logClickOpenArticle } = useAnalytics();

    const { detailsModalData, setDetailsModalData } = useContext(DetailsModalContext);

    const { storyId, cardId, chatId, scrollToTags, scrollToComments, activeCommentsInput } =
      detailsModalData;

    const detailsModalDataPrev = usePrevious(detailsModalData);

    const { id: userId, channelId } = useContext(UserContext).userInfo.userData;

    const [detailsModalHistory, setDetailsModalHistory] = useState<DetailsModalData[]>([]);

    useEffect(() => {
      if (!storyId || !cardId) {
        return;
      }

      const modalContent = document.activeElement?.closest<HTMLElement>('.modal-content');

      if (detailsModalDataPrev?.cardId && modalContent && isKeyboardFocus()) {
        modalContent.focus({ preventScroll: true });
      }

      if (chatId) {
        dispatch(getChatCardModal({ chatId, cardId }));
      } else {
        dispatch(getCardModal({ storyId, cardId }));
      }

      logClickOpenArticle({
        user_Id: sha1(`${userId}`),
        channel_id: channelId,
        story_id: storyId,
        item_id: cardId,
        time_stamp: new Date().toISOString(),
      });

      if (detailsModalDataPrev?.storyId && detailsModalDataPrev?.cardId) {
        setDetailsModalHistory([...detailsModalHistory, detailsModalDataPrev]);
      }

      return () => setDetailsModalHistory([]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardId, storyId]);

    const { card, isFetched } = useAppSelector(({ cardDetailsModal }) => cardDetailsModal);

    const historyBackHandler = useCallback(() => {
      if (!detailsModalHistory.length) {
        return;
      }

      const detailsModalHistoryCopy = [...detailsModalHistory];

      const detailsModalDataPrev = detailsModalHistoryCopy.pop();

      if (!detailsModalDataPrev) {
        return;
      }

      setDetailsModalData(detailsModalDataPrev);

      setTimeout(() => setDetailsModalHistory(detailsModalHistoryCopy), 0);
    }, [detailsModalHistory, setDetailsModalData, setDetailsModalHistory]);

    const body = useMemo(() => {
      if (!card) {
        return <Skeleton height={200} width={'100%'} />;
      }

      return (
        <>
          {Boolean(detailsModalHistory.length) && (
            <IconLabel
              className={classes['back']}
              iconId={'arrow-rounded-left'}
              iconSize={18}
              ariaLabel={t('common.back')}
              onClick={historyBackHandler}
              color={'#888888'}
              singleColor
            />
          )}
          <ContentComponent
            card={card}
            chatId={chatId}
            scrollToTags={scrollToTags}
            scrollToComments={scrollToComments}
            activeCommentsInput={activeCommentsInput}
            commentsWrapper={document.querySelector('.modal-content') as HTMLElement}
          />
        </>
      );
    }, [
      ContentComponent,
      activeCommentsInput,
      card,
      chatId,
      detailsModalHistory.length,
      historyBackHandler,
      scrollToComments,
      scrollToTags,
      t,
    ]);

    if (isFetched && !card) {
      return null;
    }

    return (
      <Modal
        isOpen={Boolean(storyId && cardId)}
        body={body}
        onClose={() => setDetailsModalData({ storyId: null, cardId: null })}
        ariaLabel={card ? getStoryCardName({ card, t }) : t('common.loading')}
      />
    );
  }
);
